import React, { memo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MuiMenu from "../framework/muiMenu";
import AddIcon from "@mui/icons-material/Add";
import RouteIcon from "@mui/icons-material/Route";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DayBookingDialog from "./DayBookingDialog";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { useAppDispatch } from "../../framework/customStore";
import { fetchDayBookings } from "../../store/workShiftPlanningSlice";

interface DateActionsProps {
    date: string;
    targetId?: string;
    selectedCell?: { day: string; targetId: string } | null;
    onCellSelect?: (cell: { day: string; targetId: string }, event?: React.MouseEvent) => void;
    employees: EmployeeListItemDto[];
    timelineStart: string;
    timelineEnd: string;
}

export const DateActions = memo(({
    date,
    targetId,
    selectedCell,
    onCellSelect,
    employees,
    timelineStart,
    timelineEnd
}: DateActionsProps) => {
    const { pathname } = useLocation();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { t } = useTranslation();

    const isSelected = selectedCell?.day === date && selectedCell?.targetId === targetId;

    const handleAddShiftForDateMenu = () => history.push(`${pathname}/new/${date}/${targetId ?? ""}`);
    const handleAddRouteForDateMenu = () => history.push(`${pathname}/route/new`);
    const handleCellClick = (e: React.MouseEvent) => {
        onCellSelect({ day: date, targetId }, e);
    };

    const [dayBookingDialogOpen, setDayBookingDialogOpen] = useState(false);
    const openDayBookingDialog = () => setDayBookingDialogOpen(true);
    const closeDayBookingDialog = () => setDayBookingDialogOpen(false);

    return (
        <div
            style={{
                backgroundColor: isSelected ? "rgba(33, 150, 243, 0.12)" : "transparent",
                border: "2px solid transparent",
                boxShadow: isSelected ? "0 0 0 1px #2196f3" : "none",
                display: "flex",
                alignItems: "center",
                padding: "4px",
                cursor: "pointer",
            }}
            onClick={handleCellClick}
        >
            <div
                title={t("workShiftPlanning.newShift")}
                onClick={handleAddShiftForDateMenu}
                style={{
                    padding: "2px 6px",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#1976d2",
                    display: "inline-block",
                    cursor: "pointer",
                    userSelect: "none",
                }}
            >
                +
            </div>

            <MuiMenu
                label={
                    <div title={t("common.moreActions")} style={{ padding: "2px 6px", display: "inline-flex", alignItems: "center" }}>
                        <KeyboardArrowDownIcon fontSize="small" />
                    </div>
                }
                items={[
                    { label: t("common.newDayBooking"), icon: <AddIcon />, onClick: openDayBookingDialog },
                    { label: t("route.newRoute"), icon: <RouteIcon />, onClick: handleAddRouteForDateMenu },
                ]}
                size="small"
                buttonSx={{ p: 0, ml: 1 }}
            />
            <div style={{ flex: 1 }} />
            <DayBookingDialog
                open={dayBookingDialogOpen}
                onClose={closeDayBookingDialog}
                employeeId={targetId}
                date={date}
                onSaved={() => {
                    const employeeIds = (employees || [])?.map(e => e.id)?.filter(Boolean);
                    if (employeeIds.length) {
                        void dispatch(fetchDayBookings({
                            startDate: timelineStart,
                            endDate: timelineEnd,
                            employeeIds
                        }) as any);
                    }
                }}
            />
        </div>
    );
});
